/**
 * =========================================================
 * features/ads/hooks.ts — TanStack Query hooks per Ads
 * =========================================================
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveAdRequest, createAd, deleteAd, getActiveAds, getAdminAdRequests, getAdminAds,
  toggleAd, trackAdClick, trackAdImpression, updateAd,
  type AdCreateIn, type AdCreativeOut, type AdUpdateIn,
} from "@/features/ads/api";
import { useAuthStore } from "@/lib/auth/authStore";

export const useActiveAds = (placement: string) =>
  useQuery<AdCreativeOut[]>({
    queryKey: ["ads", "active", placement],
    queryFn:  () => getActiveAds(placement),
    staleTime: 60_000,
  });

export const useAdminAds = () =>
  useQuery({
    queryKey: ["ads", "admin"],
    queryFn:  getAdminAds,
    staleTime: 30_000,
  });

export const useAdminAdRequests = () =>
  useQuery({
    queryKey: ["ads", "admin", "requests"],
    queryFn:  () => getAdminAdRequests(),
    staleTime: 30_000,
  });

export const useApproveAdRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: number) => approveAdRequest(requestId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ads"] });
      void qc.invalidateQueries({ queryKey: ["showcase-home"] });
    },
  });
};

export const useTrackAdClick = () => {
  const accountId = useAuthStore(s => s.user?.accountId);
  return useMutation({
    mutationFn: ({ creative_id, placement }: { creative_id: number; placement: string }) =>
      trackAdClick(creative_id, placement, accountId ? Number(accountId) : undefined),
  });
};

export const useTrackAdImpression = () => {
  const accountId = useAuthStore(s => s.user?.accountId);
  return useMutation({
    mutationFn: ({ creative_id, placement }: { creative_id: number; placement: string }) =>
      trackAdImpression(creative_id, placement, accountId ? Number(accountId) : undefined),
  });
};

export const useCreateAd = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AdCreateIn) => createAd(payload),
    onSuccess:  () => {
      void qc.invalidateQueries({ queryKey: ["ads"] });
      void qc.invalidateQueries({ queryKey: ["showcase-home"] });
    },
  });
};

export const useToggleAd = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      toggleAd(id, is_active),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ads"] });
      void qc.invalidateQueries({ queryKey: ["showcase-home"] });
    },
  });
};

export const useUpdateAd = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: AdUpdateIn }) =>
      updateAd(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ads"] });
      void qc.invalidateQueries({ queryKey: ["showcase-home"] });
    },
  });
};

export const useDeleteAd = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteAd(id),
    onSuccess:  () => {
      void qc.invalidateQueries({ queryKey: ["ads"] });
      void qc.invalidateQueries({ queryKey: ["showcase-home"] });
    },
  });
};

/**
 * =========================================================
 * Fund i features/ads/hooks.ts
 * =========================================================
 */
